import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import fontRegular from "../../pdf/fonts/NotoSansTamil-Regular.ttf";
import fontBold from "../../pdf/fonts/NotoSansTamil-Bold.ttf";

// Register fonts
Font.register({
  family: "NotoSansTamil",
  src: fontRegular,
});
Font.register({
  family: "NotoSansTamilBold",
  src: fontBold,
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 11,
    fontFamily: "NotoSansTamil",
    backgroundColor: "#fff",
  },
  section: { marginBottom: 25 },
  header: {
    fontSize: 20,
    fontFamily: "NotoSansTamilBold",
    textAlign: "center",
    marginBottom: 5,
    color: "#333",
  },
  monthHeader: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 15,
    color: "#555",
    textTransform: "uppercase",
  },
  subtitle: {
    fontSize: 13,
    fontFamily: "NotoSansTamilBold",
    textAlign: "center",
    color: "#444",
    marginBottom: 5,
  },
  transactionText: {
    fontSize: 10,
    color: "#333",
    textAlign: "center",
    padding: 4,
  },
  table: {
    width: "100%",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeader: {
    backgroundColor: "#007bff",
    color: "#fff",
    fontFamily: "NotoSansTamilBold",
    fontSize: 11,
  },
  tableCell: {
    flex: 1,
    padding: 6,
    textAlign: "center",
    fontSize: 10,
    borderWidth: 1,
    borderColor: "#ddd",
    borderStyle: "solid",
  },
  tableRowAlternate: { backgroundColor: "#f9f9f9" },
  totalsRow: {
    backgroundColor: "#e0e0e0",
    fontFamily: "NotoSansTamilBold",
  },
  footer: {
    textAlign: "center",
    marginTop: 20,
    fontSize: 13,
    fontFamily: "NotoSansTamilBold",
    borderTop: "2px solid #000",
    paddingTop: 10,
    color: "#222",
  },
});

const BalanceSheetPDF = ({
  allEntries = [],
  selectedMonth,
  selectedYear,
  initialBalance = 0,
  balance = 0,
}) => {
  // Define months for the header/footer
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = months[parseInt(selectedMonth) - 1] || "";

  const generateDaybookSummary = (entries, startBalance) => {
    if (!entries || entries.length === 0) return [];

    const sortedEntries = [...entries].sort(
      (a, b) => new Date(a.transaction_date) - new Date(b.transaction_date),
    );

    const summaryMap = new Map();

    sortedEntries.forEach((entry) => {
      const date = entry.transaction_date.slice(0, 10);
      if (!summaryMap.has(date)) {
        summaryMap.set(date, { date, varavu: 0, patru: 0, transactions: [] });
      }

      const summary = summaryMap.get(date);
      const amount = parseFloat(entry.amount || 0);
      if (entry.type === "varavu") summary.varavu += amount;
      else if (entry.type === "patru") summary.patru += amount;

      summary.transactions.push({
        receipt_no: entry.receipt_no,
        description: entry.description,
        type: entry.type,
        amount: amount,
      });
    });

    let previousClosing = startBalance;

    return Array.from(summaryMap.values()).map((summary) => {
      const opening = previousClosing;
      const closing = opening + summary.varavu - summary.patru;
      previousClosing = closing;

      return {
        ...summary,
        opening,
        closing,
      };
    });
  };

  const summaryData = generateDaybookSummary(allEntries, initialBalance);

  const formatDate = (d) => {
    const date = new Date(d);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Dynamic Header */}
        <Text style={styles.header}>
          📘 Day-wise Balance Sheet: {monthName} {selectedYear}
        </Text>

        {summaryData.map((row, idx) => (
          <View key={idx} style={styles.section}>
            <Text style={styles.subtitle}>Date: {formatDate(row.date)}</Text>
            <Text style={styles.transactionText}>
              Opening: {row.opening.toLocaleString()} | Varavu:{" "}
              {row.varavu.toLocaleString()} | Patru:{" "}
              {row.patru.toLocaleString()} | Closing:{" "}
              {row.closing.toLocaleString()}
            </Text>

            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.tableCell, { flex: 1 }]}>Loan No</Text>
                <Text style={[styles.tableCell, { flex: 2 }]}>Description</Text>
                <Text style={styles.tableCell}>Varavu</Text>
                <Text style={styles.tableCell}>Patru</Text>
              </View>

              {row.transactions.map((txn, i) => (
                <View
                  style={[
                    styles.tableRow,
                    i % 2 === 0 ? styles.tableRowAlternate : null,
                  ]}
                  key={i}
                  wrap={false}
                >
                  <Text
                    style={[styles.tableCell, { flex: 1, textAlign: "center" }]}
                  >
                    {txn.receipt_no || "-"}
                  </Text>
                  <Text
                    style={[styles.tableCell, { flex: 2, textAlign: "left" }]}
                  >
                    {txn.description}
                  </Text>
                  <Text style={styles.tableCell}>
                    {txn.type === "varavu" ? txn.amount.toLocaleString() : "-"}
                  </Text>
                  <Text style={styles.tableCell}>
                    {txn.type === "patru" ? txn.amount.toLocaleString() : "-"}
                  </Text>
                </View>
              ))}

              <View style={[styles.tableRow, styles.totalsRow]}>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 3,
                      textAlign: "right",
                      fontFamily: "NotoSansTamilBold",
                    },
                  ]}
                >
                  Day Totals:
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { fontFamily: "NotoSansTamilBold" },
                  ]}
                >
                  {row.varavu.toLocaleString()}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { fontFamily: "NotoSansTamilBold" },
                  ]}
                >
                  {row.patru.toLocaleString()}
                </Text>
              </View>
            </View>
          </View>
        ))}

        {/* Dynamic Footer */}
        <Text style={styles.footer}>
          Closing Balance as of {monthName} {selectedYear}:{" "}
          {balance.toLocaleString()}
        </Text>
      </Page>
    </Document>
  );
};
export default BalanceSheetPDF;
